// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Dialog for cloning an existing pipeline with a new id and name.
 */
import { useEffect, useState } from 'react'
import { Input } from '@/components/ui'
import { Field, FormDialog } from '@/components/forms'

export interface ClonePipelineData {
  newPipelineId: string
  newName: string
}

interface ClonePipelineDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onClone: (data: ClonePipelineData) => void
  isPending: boolean
  /** Source pipeline name — used to seed the new name field. */
  sourceName: string
  /** Source pipeline id — used to seed the new id field. */
  sourceId: string
  /** Existing pipeline ids to validate uniqueness. */
  existingIds: Set<string>
}

export function ClonePipelineDialog({
  open,
  onOpenChange,
  onClone,
  isPending,
  sourceName,
  sourceId,
  existingIds,
}: ClonePipelineDialogProps) {
  const [newPipelineId, setNewPipelineId] = useState('')
  const [newName, setNewName] = useState('')

  useEffect(() => {
    if (open) {
      setNewPipelineId(`${sourceId}-copy`)
      setNewName(`${sourceName} (Copy)`)
    }
  }, [open, sourceId, sourceName])

  const trimmedId = newPipelineId.trim()
  const trimmedName = newName.trim()
  const idCollision = trimmedId.length > 0 && existingIds.has(trimmedId)
  const canSubmit = trimmedId.length > 0 && trimmedName.length > 0 && !idCollision

  const handleSubmit = () => {
    if (!canSubmit) return
    onClone({ newPipelineId: trimmedId, newName: trimmedName })
  }

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Clone Pipeline"
      description={
        <>
          Create a copy of <span className="font-medium">{sourceName}</span> with a new id and name.
        </>
      }
      onSubmit={handleSubmit}
      submitting={isPending}
      canSubmit={canSubmit}
      submitLabel="Clone"
      submitTestId="builder-pipeline-clone-submit"
    >
      <Field label="Name" htmlFor="clone-name">
        <Input
          id="clone-name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          disabled={isPending}
          required
          autoFocus
          data-testid="builder-pipeline-clone-name"
        />
      </Field>
      <Field
        label="Pipeline ID"
        htmlFor="clone-id"
        helper={
          idCollision ? (
            <span className="text-destructive">
              A pipeline with id &ldquo;{trimmedId}&rdquo; already exists.
            </span>
          ) : (
            'Use lowercase letters, numbers, and hyphens.'
          )
        }
      >
        <Input
          id="clone-id"
          value={newPipelineId}
          onChange={(e) => setNewPipelineId(e.target.value)}
          disabled={isPending}
          required
          data-testid="builder-pipeline-clone-id"
        />
      </Field>
    </FormDialog>
  )
}
